import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Phone, KeyRound, Unlink, UserCog, Trash2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import {
  getMyParentProfile, updateMyPhone, updateMyPassword, unlinkMyChild, updateMyProfile,
} from "@/lib/api/parent-profile.functions";
import { deleteMyAccount } from "@/lib/api/account.functions";
import { supabase } from "@/integrations/supabase/client";

import { listMyChildren } from "@/lib/api/parent-students.functions";

export const Route = createFileRoute("/_authenticated/responsavel/perfil")({
  component: ParentProfilePage,
  head: () => ({
    meta: [
      { title: "Meu Perfil - FutBux" },
      { name: "description", content: "Gerencie seus dados, senha e vínculos com atletas." },
      { property: "og:title", content: "Meu Perfil - FutBux" },
      { property: "og:description", content: "Gerencie seus dados, senha e vínculos com atletas." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
});

function ParentProfilePage() {
  const getProfile = useServerFn(getMyParentProfile);
  const updPhone = useServerFn(updateMyPhone);
  const updProfile = useServerFn(updateMyProfile);
  const updPw = useServerFn(updateMyPassword);
  const unlink = useServerFn(unlinkMyChild);
  const listChildren = useServerFn(listMyChildren);
  const delAccount = useServerFn(deleteMyAccount);

  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState<string | null>(null);
  const [parents, setParents] = useState<any[]>([]);
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [cpf, setCpf] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [profileEmail, setProfileEmail] = useState("");
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [children, setChildren] = useState<Array<{ id: string; name: string; tc_name: string }>>([]);
  const [savingPhone, setSavingPhone] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPw, setSavingPw] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);


  async function load() {
    setLoading(true);
    try {
      const p = await getProfile();
      setEmail(p.email);
      setParents(p.parents);
      const first = p.parents[0] as any;
      setPhone(first?.phone || "");
      setName(first?.name || "");
      setCpf(first?.cpf || "");
      setBirthDate(first?.birth_date || "");
      setProfileEmail(first?.email || p.email || "");
      // gather children across CTs
      const all: Array<{ id: string; name: string; tc_name: string }> = [];

      for (const par of p.parents) {
        try {
          const c = await listChildren({ data: { tcId: par.tc_id } });
          for (const s of c.students) all.push({ id: s.id, name: s.name, tc_name: par.tc_name });
        } catch { /* ignore */ }
      }
      setChildren(all);
    } catch (e: any) {
      toast.error(e?.message || "Falha ao carregar perfil");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  async function savePhone() {
    if (!phone.trim()) return toast.error("Informe um telefone");
    setSavingPhone(true);
    try {
      await updPhone({ data: { phone: phone.trim() } });
      toast.success("Telefone atualizado");
      load();
    } catch (e: any) {
      toast.error(e?.message || "Erro ao salvar");
    } finally {
      setSavingPhone(false);
    }
  }

  async function saveProfile() {
    if (!name.trim()) return toast.error("Informe o nome");
    if (profileEmail && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(profileEmail))
      return toast.error("E-mail inválido");
    setSavingProfile(true);
    try {
      await updProfile({ data: {
        name: name.trim(),
        cpf: cpf.trim() || null,
        birth_date: birthDate || null,
        email: profileEmail.trim() || null,
      } });
      toast.success("Dados atualizados");
      load();
    } catch (e: any) {
      toast.error(e?.message || "Erro ao salvar");
    } finally {
      setSavingProfile(false);
    }
  }



  async function savePassword() {
    if (pw1.length < 6) return toast.error("Mínimo 6 caracteres");
    if (pw1 !== pw2) return toast.error("Senhas não coincidem");
    setSavingPw(true);
    try {
      await updPw({ data: { new_password: pw1 } });
      toast.success("Senha alterada");
      setPw1(""); setPw2("");
    } catch (e: any) {
      toast.error(e?.message || "Erro ao alterar senha");
    } finally {
      setSavingPw(false);
    }
  }

  async function doUnlink(sid: string) {
    try {
      await unlink({ data: { student_id: sid } });
      toast.success("Aluno desvinculado");
      load();
    } catch (e: any) {
      toast.error(e?.message || "Erro ao desvincular");
    }
  }

  async function doDeleteAccount() {
    if (deleteConfirm.trim().toUpperCase() !== "EXCLUIR") {
      return toast.error('Digite EXCLUIR para confirmar.');
    }
    setDeleting(true);
    try {
      await delAccount({ data: { confirmation: deleteConfirm.trim() } });
      toast.success("Conta excluída permanentemente.");
      try { await supabase.auth.signOut(); } catch { /* sessão já inválida */ }
      window.location.href = "/";
    } catch (e: any) {
      toast.error(e?.message || "Erro ao excluir conta");
      setDeleting(false);
    }
  }



  if (loading) {
    return <div className="p-6 text-muted-foreground">Carregando…</div>;
  }

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-3xl mx-auto">
      <div className="flex items-center gap-2">
        <UserCog className="h-6 w-6 text-primary" />
        <h1 className="text-2xl font-bold">Meu Perfil</h1>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <UserCog className="h-4 w-4" /> Dados pessoais
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label>Nome completo</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label>CPF</Label>
              <Input value={cpf} onChange={(e) => setCpf(e.target.value)} placeholder="000.000.000-00" />
            </div>
            <div>
              <Label>Data de nascimento</Label>
              <Input type="date" value={birthDate ?? ""} onChange={(e) => setBirthDate(e.target.value)} />
            </div>
          </div>
          <div>
            <Label>E-mail de contato</Label>
            <Input type="email" value={profileEmail} onChange={(e) => setProfileEmail(e.target.value)} />
            <p className="text-xs text-muted-foreground mt-1">
              Alterar o e-mail exige confirmação pelo link enviado ao novo endereço.
            </p>
          </div>
          <div className="flex justify-end">
            <Button onClick={saveProfile} disabled={savingProfile}>
              {savingProfile ? "Salvando…" : "Salvar dados"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>

        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Phone className="h-4 w-4" /> Dados de contato
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label>E-mail (login)</Label>
            <Input value={email ?? ""} disabled />
          </div>
          <div>
            <Label>WhatsApp / Telefone</Label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)}
              placeholder="(11) 99999-9999" />
            <p className="text-xs text-muted-foreground mt-1">
              O centro de treinamento usa este número para enviar convocações, súmulas e cobranças.
            </p>
          </div>
          <div className="flex justify-end">
            <Button onClick={savePhone} disabled={savingPhone}>
              {savingPhone ? "Salvando…" : "Salvar telefone"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <KeyRound className="h-4 w-4" /> Trocar senha
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label>Nova senha</Label>
            <Input type="password" value={pw1} onChange={(e) => setPw1(e.target.value)} />
          </div>
          <div>
            <Label>Confirmar nova senha</Label>
            <Input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} />
          </div>
          <div className="flex justify-end">
            <Button onClick={savePassword} disabled={savingPw}>
              {savingPw ? "Salvando…" : "Alterar senha"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Unlink className="h-4 w-4" /> Meus vínculos
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {children.length === 0 && (
            <p className="text-sm text-muted-foreground">Nenhum aluno vinculado.</p>
          )}
          {children.map((c) => (
            <div key={c.id} className="flex items-center justify-between border rounded p-3">
              <div>
                <div className="font-medium">{c.name}</div>
                <Badge variant="outline" className="mt-1">{c.tc_name}</Badge>
              </div>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Unlink className="h-4 w-4 mr-1" /> Desvincular
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Desvincular {c.name}?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Você deixará de visualizar as informações deste aluno. O cadastro
                      permanece no centro de treinamento; para religar, solicite um novo convite.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction onClick={() => doUnlink(c.id)}>
                      Sim, desvincular
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card className="border-destructive/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base text-destructive">
            <ShieldAlert className="h-4 w-4" /> Excluir minha conta
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            A exclusão é <strong>permanente e imediata</strong>. Seu login, seu perfil e o vínculo
            com as fichas do centro de treinamento serão apagados. Registros administrativos do
            centro (matrículas, contratos, pagamentos e presenças) permanecem com o centro de
            treinamento, sem ligação com a sua conta.
          </p>
          <div>
            <Label>Digite EXCLUIR para confirmar</Label>
            <Input
              value={deleteConfirm}
              onChange={(e) => setDeleteConfirm(e.target.value)}
              placeholder="EXCLUIR"
            />
          </div>
          <div className="flex justify-end">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="destructive"
                  disabled={deleting || deleteConfirm.trim().toUpperCase() !== "EXCLUIR"}
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  {deleting ? "Excluindo…" : "Excluir minha conta permanentemente"}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Excluir sua conta definitivamente?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Esta ação não pode ser desfeita. Você perderá o acesso ao aplicativo e será
                    necessário criar uma nova conta para voltar a usar o FutBux.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction onClick={doDeleteAccount}>
                    Sim, excluir permanentemente
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
